import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ChainedBackend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

export enum Language {
  CS = 'cs-CZ',
  SK = 'sk-SK',
  EN = 'en-US',
}

export type SupportedLanguage = Language;

// Detect initial language: Priority 1: URL, Priority 2: Fallback
const detectLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    // 1. Check URL path (e.g. "/sk/smartbanking")
    const pathSegments = window.location.pathname.split('/');
    const urlLang = pathSegments[1]; // first segment after leading slash

    if (urlLang === 'cs') return Language.CS;
    if (urlLang === 'sk') return Language.SK;
    if (urlLang === 'en') return Language.EN;
  }

  return Language.CS;
};

const initialLanguage = detectLanguage();

export type I18nVersions =
  | string
  | {
      cs?: string;
      sk?: string;
      en?: string;
    };

export const initI18n = (appVersion?: I18nVersions) => {
  if (i18n.isInitialized) return i18n;

  const defaultVersion = Math.random().toString(36).substring(2, 10);
  let resolvedVersions = {
    cs: defaultVersion,
    sk: defaultVersion,
    en: defaultVersion,
  };

  if (typeof appVersion === 'string' && appVersion) {
    resolvedVersions = { cs: appVersion, sk: appVersion, en: appVersion };
  } else if (typeof appVersion === 'object' && appVersion !== null) {
    resolvedVersions = {
      cs: appVersion.cs || defaultVersion,
      sk: appVersion.sk || defaultVersion,
      en: appVersion.en || defaultVersion,
    };
  }

  i18n
    .use(ChainedBackend)
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      fallbackLng: 'cs',
      load: 'languageOnly',
      debug: false,
      ns: ['ib'],
      defaultNS: 'ib',
      backend: {
        backends: [LocalStorageBackend, HttpBackend],
        backendOptions: [
          {
            prefix: 'penta-i18n-',
            expirationTime: 30 * 24 * 60 * 60 * 1000, // 30 days
            versions: resolvedVersions,
          },
          {
            loadPath: '/locales/{{lng}}/{{ns}}.json',
            saveMissing: true,
          },
        ],
      },
      interpolation: {
        escapeValue: false,
      },
    })
    .then((t) => {
      /* empty */
    });

  return i18n;
};

export type $Dictionary<T = unknown> = { [key: string]: T };

export default i18n;
